/**
 * ============================================================================
 * usePrefetch - Prefetch data for instant tab switches
 * ============================================================================
 *
 * Loads tab data in the background before user clicks, providing instant
 * navigation experience. Uses TanStack Query's prefetchQuery.
 *
 * CRITICAL RULES:
 * 1. Query keys in prefetch MUST exactly match the hooks that consume them,
 *    including undefined values for optional params.
 * 2. The queryFn data shape MUST exactly match what the consuming hook's
 *    queryFn returns. If a hook wraps API data (e.g. { venue, courts }),
 *    the prefetch must do the same wrapping.
 * 3. Don't prefetch tabs where the hook does complex data transformation
 *    (e.g. useTransactionsData maps raw ledger entries). Storing wrong-shaped
 *    data under the hook's key is WORSE than not prefetching at all.
 *
 * Usage:
 * - Call prefetchTab('revenue') when hovering over Revenue tab
 * - Call prefetchCommonTabs() on dashboard load for eager loading
 * ============================================================================
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { vendorApi } from '../services/api';
import { transformLedgerResponse } from './useTransactionsData';

// Set to true to enable debug logging in console
const DEBUG_PREFETCH = false;

const logPrefetch = (action: string, tab: string, details?: any) => {
  if (!DEBUG_PREFETCH) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  const style = action === 'START'
    ? 'color: #3b82f6; font-weight: bold'
    : action === 'DONE'
    ? 'color: #22c55e; font-weight: bold'
    : action === 'CACHE_HIT'
    ? 'color: #f59e0b; font-weight: bold'
    : 'color: #6b7280';

  console.log(
    `%c[${timestamp}] 🔄 PREFETCH ${action}: ${tab}`,
    style,
    details || ''
  );
};

type TabName =
  | 'overview'
  | 'dashboard'
  | 'bookings'
  | 'revenue'
  | 'transactions'
  | 'courts'
  | 'venue-info'
  | 'promotions'
  | 'customer-insights'
  | 'memberships'
  | 'reviews';

/**
 * Hook for prefetching tab data
 *
 * @param venueId - The venue ID
 * @param venueSlug - The venue slug (for memberships)
 * @param userId - The user ID (needed for venue-info query key)
 */
export function usePrefetch(venueId: number | undefined, venueSlug?: string, userId?: number) {
  const queryClient = useQueryClient();

  /**
   * Prefetch data for a specific tab
   */
  const prefetchTab = useCallback(
    async (tab: TabName) => {
      if (!venueId) return;

      const startTime = performance.now();
      logPrefetch('START', tab, { venueId });

      const staleTime = 60 * 1000; // Default: 1 minute freshness for prefetch

      // Check if data is already in cache
      const checkCache = (queryKey: any[]) => {
        const cached = queryClient.getQueryData(queryKey);
        if (cached) {
          logPrefetch('CACHE_HIT', tab, { queryKey, hasData: !!cached });
          return true;
        }
        return false;
      };

      try {
        switch (tab) {
          case 'overview': {
            // Key matches Overview.tsx: ['overview', venueId, sourceFilter]
            // Default sourceFilter is 'all'
            // Overview has staleTime: 0 (always stale), but prefetch gives instant
            // placeholder via placeholderData while background refetch runs
            const overviewKey = ['overview', venueId, 'all'];
            if (!checkCache(overviewKey)) {
              await queryClient.prefetchQuery({
                queryKey: overviewKey,
                queryFn: () => vendorApi.getOverview(venueId, 'all'),
                staleTime: 0, // Match component staleTime
              });
            }
            break;
          }

          case 'dashboard': {
            if (!checkCache(['dashboard'])) {
              await queryClient.prefetchQuery({
                queryKey: ['dashboard'],
                queryFn: () => vendorApi.getDashboard(),
                staleTime,
              });
            }
            break;
          }

          case 'bookings': {
            // Prefetch today's schedule for availability view
            const today = new Date().toISOString().split('T')[0];
            const bookingsKey = ['schedule', venueId, today];
            if (!checkCache(bookingsKey)) {
              await queryClient.prefetchQuery({
                queryKey: bookingsKey,
                queryFn: () => vendorApi.getSchedule(venueId, { date: today }),
                staleTime,
              });
            }
            break;
          }

          case 'revenue': {
            // CRITICAL: Key must EXACTLY match useRevenueData default key
            // Component uses: ['earnings', venueId, dateFrom, dateTo, source]
            // Defaults: dateFrom=undefined, dateTo=undefined, source='all'
            const revenueKey = ['earnings', venueId, undefined, undefined, 'all'];
            if (!checkCache(revenueKey)) {
              await queryClient.prefetchQuery({
                queryKey: revenueKey,
                queryFn: () => vendorApi.getEarningsSummary(venueId, 'all'),
                staleTime: 2 * 60 * 1000, // Match component staleTime
              });
            }
            break;
          }

          case 'transactions': {
            // CRITICAL: Uses shared transformLedgerResponse to produce the exact
            // same { transactions, stats, totalCount } shape as useTransactionsData
            // Default filters: type='all', dateRange=0 (all time)
            const transactionsKey = ['ledger-transactions', venueId, 'all', 0];
            if (!checkCache(transactionsKey)) {
              await queryClient.prefetchQuery({
                queryKey: transactionsKey,
                queryFn: async () => {
                  const response = await vendorApi.getLedgerEntries(venueId, { per_page: 500, days: 0 });
                  return transformLedgerResponse(response);
                },
                staleTime: 2 * 60 * 1000, // Match component staleTime
              });
            }
            break;
          }

          case 'courts': {
            // CRITICAL: Data shape must match useCourtsData hook queryFn return
            // Hook returns: { venue: { id, name, sports }, courts }
            const courtsKey = ['courts', venueId];
            if (!checkCache(courtsKey)) {
              await queryClient.prefetchQuery({
                queryKey: courtsKey,
                queryFn: async () => {
                  const courts = await vendorApi.getCourts(venueId);
                  // Wrap to match useCourtsData queryFn shape
                  return {
                    venue: {
                      id: venueId,
                      name: 'Venue',
                      sports: [],
                    },
                    courts,
                  };
                },
                staleTime: 2 * 60 * 1000, // Match component staleTime
              });
            }
            break;
          }

          case 'venue-info': {
            // Prefetch the main venue-info query (matches useVenueData hook)
            // Key: ['venue-info', userId, venueId]
            // Shape: { venue, blackoutDates, courts }
            if (userId) {
              const venueInfoKey = ['venue-info', userId, venueId];
              if (!checkCache(venueInfoKey)) {
                await queryClient.prefetchQuery({
                  queryKey: venueInfoKey,
                  queryFn: async () => {
                    const dashboardData = await vendorApi.getDashboard();
                    const venue = dashboardData.venues?.[0];
                    if (!venue) throw new Error('No venue found');
                    const [blackoutDatesData, courtsData] = await Promise.all([
                      vendorApi.getBlackoutDates(venue.id).catch(() => []),
                      vendorApi.getCourts(venue.id).catch(() => []),
                    ]);
                    return {
                      venue,
                      blackoutDates: Array.isArray(blackoutDatesData)
                        ? blackoutDatesData
                        : ((blackoutDatesData as any)?.data || []),
                      courts: courtsData,
                    };
                  },
                  staleTime: 5 * 60 * 1000, // Match useVenueData staleTime
                });
              }
            }
            break;
          }

          case 'promotions': {
            const promotionsKey = ['promotions', venueId];
            if (!checkCache(promotionsKey)) {
              await queryClient.prefetchQuery({
                queryKey: promotionsKey,
                queryFn: () => vendorApi.getPromotions(venueId),
                staleTime,
              });
            }
            break;
          }

          case 'customer-insights': {
            // CRITICAL: Key must match useCustomerInsights hook
            // Hook uses: ['customerInsights', ...] (camelCase, NOT hyphenated)
            // Shape must match: { insights, allCustomers }
            const insightsKey = ['customerInsights', venueId, 'month', 'all'];
            if (!checkCache(insightsKey)) {
              await queryClient.prefetchQuery({
                queryKey: insightsKey,
                queryFn: async () => {
                  const insights = await vendorApi.getCustomerInsights(venueId, 'month', 'all');
                  const allCustomers = insights.customers || insights.top_customers || [];
                  // Must match useCustomerInsights queryFn shape for client-side filtering
                  return { insights, allCustomers };
                },
                staleTime: 15 * 60 * 1000, // Match component staleTime (15 min)
              });
            }
            break;
          }

          // 'memberships' prefetch removed Jul 2026: it hit the deleted
          // legacy plans endpoint and no page ever read its cache key.

          case 'reviews': {
            const reviewsKey = ['reviews', venueId];
            if (!checkCache(reviewsKey)) {
              await queryClient.prefetchQuery({
                queryKey: reviewsKey,
                queryFn: () => vendorApi.getReviews(venueId),
                staleTime,
              });
            }
            break;
          }
        }

        const endTime = performance.now();
        const duration = (endTime - startTime).toFixed(1);
        logPrefetch('DONE', tab, { duration: `${duration}ms` });
      } catch (error) {
        const endTime = performance.now();
        const duration = (endTime - startTime).toFixed(1);
        logPrefetch('ERROR', tab, { duration: `${duration}ms`, error });
      }
    },
    [queryClient, venueId, venueSlug, userId]
  );

  /**
   * Prefetch common tabs in background (call on dashboard load)
   * Staggered with setTimeout to avoid blocking initial render.
   *
   * `canPrefetch` (staff-access): skip tabs the current user can't access —
   * a staff member without the money/insights capabilities would otherwise
   * fire background 403s (and trip rate limits) for owner-only endpoints.
   * Owners pass no predicate and prefetch everything, as before.
   */
  const prefetchCommonTabs = useCallback(async (canPrefetch?: (tab: TabName) => boolean) => {
    if (!venueId) return;

    const allow = (tab: TabName) => !canPrefetch || canPrefetch(tab);

    // Order: most likely to be visited first
    if (allow('overview')) setTimeout(() => prefetchTab('overview'), 100);
    if (allow('bookings')) setTimeout(() => prefetchTab('bookings'), 200);
    if (allow('revenue')) setTimeout(() => prefetchTab('revenue'), 300);
    if (allow('courts')) setTimeout(() => prefetchTab('courts'), 400);
    if (allow('transactions')) setTimeout(() => prefetchTab('transactions'), 500);
    if (allow('customer-insights')) setTimeout(() => prefetchTab('customer-insights'), 600);
  }, [venueId, prefetchTab]);

  /**
   * Prefetch on hover handler - attach to nav items
   */
  const createHoverHandler = useCallback(
    (tab: TabName) => {
      let timeoutId: NodeJS.Timeout;

      return {
        onMouseEnter: () => {
          logPrefetch('HOVER_START', tab, { note: 'Will prefetch in 100ms if mouse stays' });
          timeoutId = setTimeout(() => prefetchTab(tab), 100);
        },
        onMouseLeave: () => {
          logPrefetch('HOVER_CANCEL', tab, { note: 'Mouse left before prefetch started' });
          clearTimeout(timeoutId);
        },
      };
    },
    [prefetchTab]
  );

  return {
    prefetchTab,
    prefetchCommonTabs,
    createHoverHandler,
  };
}
